import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import RadioGroup from './components/RadioGroup';
import LabeledInput from './components/LabeledInput';
// import SectionDivider from './components/SectionDivider'; // Uncomment when spouse section is needed
import Toggle from './components/Toggle';
import FileUpload from './components/FileUpload';
import type { SupportedFormData } from './schemas/supportedFormSchema';
import { toHebrewName } from '@/helpers/hebrewName';
// import userIcon from '@/assets/icons/types/Type=User.svg'; // Uncomment when spouse section is needed
import babyIcon from '@/assets/icons/types/Type=Baby.svg';
import beachIcon from '@/assets/icons/types/Type=Beach.svg';
import minusIcon from '@/assets/icons/minus.svg';
import addIcon from '@/assets/icons/add.svg';
import closeIcon from '@/assets/icons/close.svg';
import { MIN_BIRTH_DATE_INPUT } from '@/helpers/dateHelper';
import { SUPPORTED_PERSON_AUTOCOMPLETE, childFieldDom } from './supportedFormAutofill';

const FamilyInfo = () => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext<SupportedFormData>();

  // const spouse = watch('spouse'); // Uncomment when spouse section is needed

  const {
    fields: children,
    append: appendChild,
    remove: removeChild,
  } = useFieldArray({
    control,
    name: 'children',
  });

  // Documents from form state (shared for all children)
  const documents = watch('childrenDocuments') || [];

  /*
   * HAT-1464: the children of a deleted supported entered again. Each is a contact of their own,
   * already reviewed by the office, so they are kept or dropped - not edited here. A wrong one is
   * dropped and added again below as a new child.
   */
  const existingChildren = watch('existingChildren') || [];
  const hasAnyChild = children.length > 0 || existingChildren.length > 0;

  const handleRemoveExistingChild = (childId: number) => {
    setValue(
      'existingChildren',
      existingChildren.filter((child) => child.id !== childId),
    );
  };

  const handleFileAdd = (file: File | null) => {
    if (!file) return;
    setValue('childrenDocuments', [...documents, file]);
  };

  const handleFileRemove = (fileIndex: number) => {
    setValue(
      'childrenDocuments',
      documents.filter((_, i) => i !== fileIndex)
    );
  };

  /*Uncomment when spouse section is needed
  const handleSpouseChange = (value: string) => {
    const hasSpouse = value === 'true';
    setValue('spouse', hasSpouse ? {
      id: 0,
      firstName: '',
      israeliId: '',
    } : null);
  };
  */

  const handleChildrenChange = (value: string) => {
    const hasChildren = value === 'true';
    if (hasChildren && !hasAnyChild) {
      appendChild({
        id: 0,
        firstName: '',
        israeliId: '',
        birthDate: undefined,
        livesWithSupported: true,
      });
    } else if (!hasChildren) {
      for (let i = children.length - 1; i >= 0; i--) {
        removeChild(i);
      }
      setValue('existingChildren', []);
    }
  };

  const handleAddChild = () => {
    appendChild({
      id: 0,
      firstName: '',
      israeliId: '',
      birthDate: undefined,
      livesWithSupported: true,
    });
  };

  const ac = SUPPORTED_PERSON_AUTOCOMPLETE;

  const renderChildFields = (index: number) => (
    <>
      <Controller
        name={`children.${index}.firstName`}
        control={control}
        render={({ field }) => {
          const dom = childFieldDom(index, 1);
          return (
            <LabeledInput
              label={t('firstName')}
              id={dom.id}
              name={dom.name}
              autoComplete={ac}
              mobileFullWidth
              value={field.value}
              onChange={(e) => field.onChange(toHebrewName(e.target.value))}
              required
            />
          );
        }}
      />
      <Controller
        name={`children.${index}.israeliId`}
        control={control}
        render={({ field }) => {
          const dom = childFieldDom(index, 2);
          return (
            <LabeledInput
              label={t('israeliId')}
              id={dom.id}
              name={dom.name}
              autoComplete={ac}
              mobileFullWidth
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              required
            />
          );
        }}
      />
      <Controller
        name={`children.${index}.birthDate`}
        control={control}
        render={({ field }) => {
          const dom = childFieldDom(index, 3);
          return (
            <LabeledInput
              label={t('birthDate')}
              id={dom.id}
              name={dom.name}
              autoComplete={ac}
              type="date"
              min={MIN_BIRTH_DATE_INPUT}
              mobileFullWidth
              value={field.value ? field.value.toISOString().split('T')[0] : ''}
              onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value) : undefined)}
              required
            />
          );
        }}
      />
      <Controller
        name={`children.${index}.livesWithSupported`}
        control={control}
        render={({ field }) => <Toggle label={t('livesWithSupportedQuestion')} value={field.value} onChange={(value) => field.onChange(value)} />}
      />
    </>
  );

  return (
    <div className="flex flex-col gap-6 w-[700px] max-md:w-full">
      {/* Spouse Section - Uncomment when spouse section is needed */}
      {/* <div className="flex flex-col gap-4">
        <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('hasSpouse')}</h4>
        <RadioGroup
          name="hasSpouse"
          value={spouse !== null}
          onChange={handleSpouseChange}
          options={[
            { value: 'true', label: t('spouseYes'), icon: userIcon },
            { value: 'false', label: t('spouseNo'), icon: beachIcon },
          ]}
        />
      </div> */}

      {/* Spouse Details - Conditional - Uncomment when spouse section is needed */}
      {/* {spouse && (
        <div className="flex flex-col gap-4">
          <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('spouseDetails')}</h4>
          <div className="flex flex-wrap gap-4 max-md:flex-col">
            <Controller
              name="spouse.israeliId"
              control={control}
              render={({ field }) => (
                <LabeledInput
                  label={t('israeliIdNumber')}
                  id="sp-s1"
                  name="sp-s1"
                  autoComplete={ac}
                  width="414px"
                  mobileFullWidth
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  required
                />
              )}
            />
            <Controller
              name="spouse.firstName"
              control={control}
              render={({ field }) => (
                <LabeledInput
                  label={t('firstName')}
                  id="sp-s2"
                  name="sp-s2"
                  autoComplete={ac}
                  width="414px"
                  mobileFullWidth
                  value={field.value || ''}
                  onChange={(e) => field.onChange(toHebrewName(e.target.value))}
                  required
                />
              )}
            />
          </div>
        </div>
      )} */}

      {/* <SectionDivider /> - Uncomment when spouse section is needed */}

      {/* Children Section */}
      <div className="flex flex-col gap-4">
        <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('hasChildren')}</h4>
        <RadioGroup
          name="hasChildren"
          value={hasAnyChild}
          onChange={handleChildrenChange}
          options={[
            { value: 'true', label: t('childrenYes'), icon: babyIcon },
            { value: 'false', label: t('childrenNo'), icon: beachIcon },
          ]}
        />
      </div>

      {/* Children Details - Conditional */}
      {hasAnyChild && (
        <div className="flex flex-col gap-6">
          <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('childrenDetails')}</h4>

          {existingChildren.length > 0 && (
            <div className="flex flex-col gap-3">
              <span className="body-md-semibold text-(--font-body-1)">{t('reenterExistingChildren')}</span>
              <span className="body-sm-regular text-(--font-body-3)">{t('reenterExistingChildrenHint')}</span>
              <ul className="flex flex-col gap-2">
                {existingChildren.map((child) => (
                  <li
                    key={child.id}
                    className="flex items-center justify-between gap-4 bg-(--surface-grey-lowest) border border-(--border-lowest) rounded-xl px-4 py-3 max-md:flex-col max-md:items-start"
                  >
                    <div className="flex flex-wrap gap-x-6 gap-y-1 body-sm-regular text-(--font-body-5)">
                      <span className="body-sm-semibold">
                        {child.firstName} {child.lastName}
                      </span>
                      <span>
                        {t('israeliId')}: {child.israeliId}
                      </span>
                      {child.birthDate && (
                        <span>
                          {t('birthDate')}: {new Date(child.birthDate).toLocaleDateString('he-IL')}
                        </span>
                      )}
                      <span className="text-(--font-body-3)">{t(child.status)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveExistingChild(child.id)}
                      className="body-sm-semibold text-(--font-error) underline shrink-0"
                      aria-label={`${t('removeChild')} ${child.firstName}`}
                    >
                      {t('removeChild')}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {children.map((child, index) => (
            <div key={child.id} className="flex flex-col gap-4">
              {/* Beside kept existing children a new one is not required, so each can be removed. */}
              {(index > 0 || existingChildren.length > 0) && (
                <div className="bg-(--surface-grey-lowest) border border-(--border-lowest) rounded-2xl p-8 max-md:p-4 relative shadow-sm">
                  <div className="flex gap-8 max-md:flex-col max-md:gap-4 items-start">
                    {renderChildFields(index)}
                    <button
                      type="button"
                      onClick={() => removeChild(index)}
                      className="absolute start-[-12px] top-6 w-6 h-6 bg-(--font-error) rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                      title={t('removeChild')}
                    >
                      <img src={minusIcon} alt="" />
                    </button>
                  </div>
                </div>
              )}

              {index === 0 && existingChildren.length === 0 && (
                <div className="flex gap-8 max-md:flex-col max-md:gap-4 items-end">{renderChildFields(index)}</div>
              )}
            </div>
          ))}

          {/* Add Another Child Button */}
          <button
            type="button"
            onClick={handleAddChild}
            className="bg-(--surface-base) border border-(--border-main) rounded-lg h-9 px-3 flex items-center justify-center gap-1 body-sm-semibold text-(--font-primary) self-start"
          >
            <div className="w-[10px] h-[10px] relative">
              <img src={addIcon} alt="" />
            </div>
            {t('addAnotherChild')}
          </button>

          {/* Documents Section - For All Children. Kept existing children already have theirs. */}
          {children.length > 0 && (
          <div className="flex flex-col gap-4">
            <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('documents')}</h4>
            <div className="w-full">
              <FileUpload placeholder={t('attachChildDocument')} value={null} onChange={handleFileAdd} accept="image/*,.pdf" />
            </div>
            {/* TODO: need refactor to use the FileUpload component */}
            {documents.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="body-sm-regular text-(--font-body-2)">{t('uploadedDocuments')}:</span>
                <div className="flex flex-wrap gap-2">
                  {documents.map((doc, docIndex) => (
                    <div key={docIndex} className="bg-(--surface-base) border border-(--border-strong) rounded h-8 px-3 flex items-center gap-2">
                      <button type="button" onClick={() => handleFileRemove(docIndex)} className="w-[10px] h-[10px] flex items-center justify-center">
                        <img src={closeIcon} width={15} height={15} alt="" />
                      </button>
                      <div className="w-0 h-[10px] border-r border-(--border-main)" />
                      <span className="body-sm-regular text-(--font-body-5)">{doc.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FamilyInfo;
